import { useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';

import { GET_ANALYTICS_DASHBOARD } from '@/modules/analytics/api/analytics.query';
import { GET_DATA_POINT_BREADCRUMB } from '@/modules/custom-data/api/data-point.query';
import { GET_DOCUMENT_BREADCRUMB } from '@/modules/documents/api/document.query';
import { GET_PRODUCT } from '@/modules/products/api/product.query';
import { GET_NEW_REPORT_BREADCRUMB } from '@/modules/report/api/newReport.query';
import { GET_SUPPLIER } from '@/modules/suppliers/api/supplier.query';

import {
  GetAnalyticsDashboardQuery,
  GetDataPointBreadcrumbQuery,
  GetDocumentBreadcrumbQuery,
  GetNewReportBreadcrumbQuery,
  GetProductQuery,
  GetSupplierQuery,
} from '../types';

export const useDynamicBreadcrumbs = (
  type:
    | 'analyticsDashboard'
    | 'document'
    | 'documentFolder'
    | 'newReport'
    | 'dataPoint'
    | 'product'
    | 'supplier',
  id: string,
  projectId: string
) => {
  const [executeAnalyticsDashboardQuery, { loading: analyticsDashboardLoading }] = useLazyQuery(
    GET_ANALYTICS_DASHBOARD,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const [executeDocumentQuery, { loading: documentLoading }] = useLazyQuery(
    GET_DOCUMENT_BREADCRUMB,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const [executeNewReportQuery, { loading: newReportLoading }] = useLazyQuery(
    GET_NEW_REPORT_BREADCRUMB,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const [executeDataPointQuery, { loading: dataPointLoading }] = useLazyQuery(
    GET_DATA_POINT_BREADCRUMB,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const [executeProductQuery, { loading: productLoading }] = useLazyQuery(GET_PRODUCT, {
    fetchPolicy: 'cache-first',
  });
  const [executeSupplierQuery, { loading: supplierLoading }] = useLazyQuery(GET_SUPPLIER, {
    fetchPolicy: 'cache-first',
  });

  const isLoading =
    analyticsDashboardLoading ||
    documentLoading ||
    newReportLoading ||
    dataPointLoading ||
    productLoading ||
    supplierLoading;

  const getBreadcrumb: () => Promise<
    {
      id: string | undefined;
      label: string | undefined;
      itemType?: string;
    }[]
  > = useCallback(async () => {
    if (type === 'analyticsDashboard') {
      const result = await executeAnalyticsDashboardQuery({ variables: { id, projectId } });
      const dashboard = (result.data as GetAnalyticsDashboardQuery | undefined)?.analyticsDashboard;
      return [{ id: dashboard?.id, label: dashboard?.title }];
    }

    if (type === 'document' || type === 'documentFolder') {
      const result = await executeDocumentQuery({
        variables: { id, projectId, type: type === 'document' ? 'DOCUMENT' : 'FOLDER' },
      });
      const breadcrumb = (result.data as GetDocumentBreadcrumbQuery | undefined)
        ?.documentBreadcrumb;
      return breadcrumb
        ? breadcrumb.map((item) => ({ id: item?.id, label: item?.name, itemType: item?.type }))
        : [];
    }

    if (type === 'newReport') {
      const result = await executeNewReportQuery({ variables: { id, projectId } });
      const newReport = (result.data as GetNewReportBreadcrumbQuery | undefined)?.newReport;
      return [{ id: newReport?.id, label: newReport?.title }];
    }

    if (type === 'product') {
      const result = await executeProductQuery({ variables: { id, projectId } });
      const product = (result.data as GetProductQuery | undefined)?.product;
      return [{ id: product?.id, label: product?.title }];
    }

    if (type === 'supplier') {
      const result = await executeSupplierQuery({ variables: { id, projectId } });
      const supplier = (result.data as GetSupplierQuery | undefined)?.supplier;
      return [{ id: supplier?.id, label: supplier?.name }];
    }

    const result = await executeDataPointQuery({ variables: { id, projectId } });
    const dataPoint = (result.data as GetDataPointBreadcrumbQuery | undefined)?.dataPoint;
    return [{ id: dataPoint?.id, label: dataPoint?.title }];
  }, [
    executeAnalyticsDashboardQuery,
    executeDataPointQuery,
    executeDocumentQuery,
    executeNewReportQuery,
    executeProductQuery,
    executeSupplierQuery,
    id,
    projectId,
    type,
  ]);

  return {
    getBreadcrumb,
    isLoading,
  };
};
